import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { api, send } from '../../api';
import { Button } from '../ui/button';
import { useAgentName } from '../../agent-name';
import type { LadderAction, LadderLevel, LadderRules, LadderStanding } from '../../../../src/domain/policy/ladder';
import './autonomy-ladder.css';

type Row = { action: LadderAction; since: string; by: string; reason: string; measured: boolean } & LadderStanding;
export interface LadderView { policyId: string; state: 'off' | 'invalid' | 'on'; rules: LadderRules | null; actions: Row[] }

const actionNames: Record<LadderAction, string> = {
  reminder_email: 'Reminder emails', promise_ack: 'Promise acknowledgements', document_copy: 'Document copies', call_recap: 'Call recaps',
};
const levelNames: Record<LadderLevel, string> = { draft: 'Draft', sample: 'Sample', auto: 'Auto' };
const levels: LadderLevel[] = ['draft', 'sample', 'auto'];
const pct = (share: number | null) => share === null ? '–' : `${Math.round(share * 100)}%`;

/** What each rung means, in the configured agent's name. */
export function levelLine(level: LadderLevel, agent: string) {
  return { draft: 'You approve each one.', sample: `${agent} sends them; you spot-check a sample.`, auto: `${agent} sends them.` }[level];
}

/**
 * Earned autonomy (#226) on the Policy page: per action type, its rung (draft, sample, auto), the reviews since it got
 * there, and whether it may move up. Owners approve a promotion or move a type back to draft. It only narrows who
 * approves: under "You approve" every rung still waits for a person.
 */
export function AutonomyLadder({ policyId, owner }: { policyId: string; owner: boolean }) {
  const agent = useAgentName();
  const [view, setView] = useState<LadderView | null>(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api<LadderView>(`/app-api/policies/${policyId}/ladder`).then(setView)
    .catch(() => setView(null)), [policyId]);
  useEffect(() => { void load(); }, [load]);
  async function run(work: () => Promise<LadderView>, done: string) {
    if (busy) return;
    setBusy(true);
    try { setView(await work()); toast.success(done); }
    catch (e) { toast.error(e instanceof Error ? e.message : 'This could not be changed.'); }
    finally { setBusy(false); }
  }
  const move = (row: Row, level: LadderLevel) => run(
    () => send<LadderView>(`/app-api/policies/${policyId}/ladder/actions/${row.action}`, 'PUT', { level }),
    `${actionNames[row.action]}: ${levelNames[level]}.`);
  if (!view) return null;
  const label = 'Earned autonomy';
  if (view.state === 'off') {
    return <section className="autonomy-ladder" aria-label={label}>
      <div className="ladder-head"><h3>{label}</h3>
        {owner && <Button size="sm" variant="outline" disabled={busy}
          onClick={() => void run(() => api<LadderView>(`/app-api/policies/${policyId}/ladder`, {}), 'Every action now starts in draft.')}>
          Start in draft</Button>}</div>
      <p className="ladder-line">Not started: the approval rules above decide alone. Starting puts every action type in draft, and
        each moves up to sample, then auto, as your reviews show {agent} gets it right.</p>
    </section>;
  }
  if (view.state === 'invalid') {
    return <section className="autonomy-ladder" aria-label={label}>
      <div className="ladder-head"><h3>{label}</h3></div>
      <div className="alert error" role="alert">The ladder could not be read, so every action waits for a person.</div>
    </section>;
  }
  const rules = view.rules!;
  return <section className="autonomy-ladder" aria-label={label}>
    <div className="ladder-head"><h3>{label}</h3></div>
    <p className="ladder-line">Moves up after {rules.promoteAfter} reviews in a row with no rejection and at most {pct(rules.maxEditRate)} edited;
      {rules.autoPromote ? ' it promotes by itself.' : ' an owner approves each promotion.'} A rejected, undone or edited send, or a
      failed spot-check, puts it back in draft.</p>
    <table className="ladder-table">
      <thead><tr><th scope="col">Action</th><th scope="col">Rung</th><th scope="col">Reviews</th><th scope="col">Edited</th>
        <th scope="col">Accepted</th><th scope="col">Next</th>{owner && <th scope="col"><span className="sr-only">Change</span></th>}</tr></thead>
      <tbody>{view.actions.map(row => <tr key={row.action} data-demoted={row.demotion ? 'true' : undefined}>
        <th scope="row">{actionNames[row.action]}</th>
        <td><span className="ladder-rungs" aria-label={`On ${levelNames[row.effective]}`}>
          {levels.map(level => <span key={level} data-on={level === row.effective ? 'true' : undefined}>{levelNames[level]}</span>)}</span>
          <span className="ladder-sub">{levelLine(row.effective, agent)}</span></td>
        <td>{row.measured ? row.stats.reviewed : '–'}</td>
        <td>{row.measured ? pct(row.stats.editRate) : '–'}</td>
        <td>{row.measured ? pct(row.stats.acceptance) : '–'}</td>
        <td className="ladder-next">{row.demotion ?? row.promotion}</td>
        {owner && <td className="ladder-actions">
          {row.next && !row.demotion && (row.promotable || !row.measured) && <Button size="sm" disabled={busy}
            onClick={() => void move(row, row.next!)}>Promote to {levelNames[row.next]}</Button>}
          {(row.level !== 'draft') && <Button size="sm" variant="outline" disabled={busy}
            onClick={() => void move(row, 'draft')}>Back to draft</Button>}
        </td>}
      </tr>)}</tbody>
    </table>
  </section>;
}
